import { db, auth } from '@/config/firebase';
import { savingsGoalsPath } from '@/config/firestoreCollections';
import { track } from '@/services/analytics';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { differenceInMonths, isAfter, isBefore } from 'date-fns';

const toTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Timestamp.fromDate(value);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
};

const fromTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const emitGoalAnalyticsEvent = (name, props = {}) => {
  try {
    const maybePromise = track(name, props);
    if (maybePromise?.catch) {
      maybePromise.catch(() => {});
    }
  } catch (_) {
    // Analytics is best-effort; ignore failures
  }
};

const DEFAULT_GOAL_THEME = {
  icon: '🎯',
  color: '#06D6A0',
};

export const GOAL_PRIORITY = ['low', 'medium', 'high'];

export const DEFAULT_NOTIFICATION_RULES = {
  enabled: true,
  channels: ['push'],
  triggers: {
    milestones: true,
    overdue: true,
    resets: false,
  },
  thresholdPercent: 0.75,
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const normalizePriority = (priority) =>
  GOAL_PRIORITY.includes(priority) ? priority : 'medium';

const normalizeTheme = (theme = {}) => ({
  icon: typeof theme.icon === 'string' && theme.icon.length ? theme.icon : DEFAULT_GOAL_THEME.icon,
  color:
    typeof theme.color === 'string' && /^#([0-9A-Fa-f]{6})$/.test(theme.color)
      ? theme.color
      : DEFAULT_GOAL_THEME.color,
});

const normalizeNotificationRules = (rules = {}, notifyFallback = true) => {
  const enabled = typeof rules.enabled === 'boolean' ? rules.enabled : notifyFallback;
  const channels = Array.isArray(rules.channels)
    ? rules.channels
        .map((channel) => (channel === 'email' ? 'email' : 'push'))
        .filter((channel, index, arr) => arr.indexOf(channel) === index)
    : [...DEFAULT_NOTIFICATION_RULES.channels];
  if (!channels.length) channels.push('push');
  return {
    enabled,
    channels,
    triggers: {
      milestones: rules.triggers?.milestones !== false,
      overdue: rules.triggers?.overdue !== false,
      resets: Boolean(rules.triggers?.resets),
    },
    thresholdPercent:
      typeof rules.thresholdPercent === 'number'
        ? clamp(rules.thresholdPercent, 0, 1)
        : DEFAULT_NOTIFICATION_RULES.thresholdPercent,
  };
};


/**
 * Goal status enum
 */
export const GOAL_STATUS = {
  ACTIVE: 'active',
  COMPLETED: 'completed',
  OVERDUE: 'overdue',
  PAUSED: 'paused',
};

export const GOAL_MODE = {
  ACCUMULATE: 'accumulate',
  LIMIT: 'limit',
};

export const GOAL_INTENTS = [
  {
    id: 'save-money',
    label: 'Save Money',
    description: 'Build a pot for something meaningful',
    icon: '💰',
    accent: '#0E9F6E',
    mode: GOAL_MODE.ACCUMULATE,
  },
  {
    id: 'spending-cap',
    label: 'Spending Cap',
    description: 'Stay under a flexible limit',
    icon: '🚦',
    accent: '#EF476F',
    mode: GOAL_MODE.LIMIT,
  },
  {
    id: 'category-limit',
    label: 'Category Limit',
    description: 'Keep a category in check',
    icon: '🗂️',
    accent: '#3A86FF',
    mode: GOAL_MODE.LIMIT,
  },
  {
    id: 'debt-paydown',
    label: 'Debt Paydown',
    description: 'Track repayments & momentum',
    icon: '📉',
    accent: '#F59E0B',
    mode: GOAL_MODE.ACCUMULATE,
  },
  {
    id: 'custom-goal',
    label: 'Custom Goal',
    description: 'Design your own target',
    icon: '✨',
    accent: '#8338EC',
    mode: GOAL_MODE.ACCUMULATE,
  },
];

export const GOAL_TIMEFRAMES = [
  {
    id: 'open',
    label: 'Ongoing',
    description: 'No fixed deadline',
  },
  {
    id: 'weekly',
    label: 'Weekly',
    description: 'Reset every week',
  },
  {
    id: 'monthly',
    label: 'Monthly',
    description: 'Reset every month',
  },
  {
    id: 'quarterly',
    label: 'Quarterly',
    description: 'Reset every quarter',
  },
  {
    id: 'custom',
    label: 'Custom Range',
    description: 'Pick exact dates',
  },
];

const normalizeIntent = (intent) =>
  GOAL_INTENTS.some((item) => item.id === intent) ? intent : GOAL_INTENTS[0].id;

const normalizeGoalMode = (mode, intent) => {
  if (Object.values(GOAL_MODE).includes(mode)) {
    return mode;
  }
  const matchingIntent = GOAL_INTENTS.find((item) => item.id === intent);
  return matchingIntent?.mode || GOAL_MODE.ACCUMULATE;
};

const normalizeTimeframe = (timeframe) =>
  GOAL_TIMEFRAMES.some((option) => option.id === timeframe) ? timeframe : 'open';

/**
 * Format goal data for creation
 * FINLIST ENVELOPE SYSTEM: Goals must include linkedEnvelopeId from Budget envelopes
 * FINLIST CFO MODEL: Goals include trackingCategories for automatic transaction sync
 */
const formatGoalForCreate = (goalData) => {
  const intent = normalizeIntent(goalData.intent || goalData.goalType);
  const goalMode = normalizeGoalMode(goalData.goalMode, intent);
  const timeframe = normalizeTimeframe(goalData.timeframe);
  const theme = normalizeTheme(goalData.theming || { icon: goalData.icon, color: goalData.color });
  const notificationRules = normalizeNotificationRules(goalData.notificationRules, goalData.notify);
  const timeframeStart = timeframe === 'custom' ? toTimestamp(goalData.timeframeStart) : null;
  const timeframeEnd = timeframe === 'custom' ? toTimestamp(goalData.timeframeEnd) : null;

  const targetAmount = Math.max(0, parseFloat(goalData.targetAmount) || 0);

  return {
    name: goalData.name,
    description: goalData.description || '',
    targetAmount,
    target: targetAmount,
    currentAmount: Math.max(0, parseFloat(goalData.currentAmount) || 0),
    current: Math.max(0, parseFloat(goalData.currentAmount) || 0),
    monthlyContribution: Math.max(0, parseFloat(goalData.monthlyContribution) || 0),
    dueDate: toTimestamp(goalData.dueDate),
    goalType: intent,
    intent,
    goalMode,
    timeframe,
    timeframeLabel: goalData.timeframeLabel || 'Ongoing goal',
    timeframeStart,
    timeframeEnd,
    status: goalData.status || GOAL_STATUS.ACTIVE,
    cadence: goalData.cadence || (timeframe === 'weekly' ? 'WEEKLY' : timeframe === 'monthly' ? 'MONTHLY' : 'ONGOING'),
    plannedPerPeriod: Math.max(0, parseFloat(goalData.plannedPerPeriod || goalData.monthlyContribution) || 0),
    lastContributionAt: goalData.lastContributionAt ? toTimestamp(goalData.lastContributionAt) : null,
    priority: normalizePriority(goalData.priority),
    category: goalData.category || goalData.categoryGroup || 'general',
    categoryGroup: goalData.categoryGroup || goalData.category || 'general',
    categorySub: goalData.categorySub || null,
    // Store categoryId for proper transaction tracking (used in addContribution)
    // Derive from categoryId, category, or categoryGroup fields
    categoryId: goalData.categoryId || 
                (goalData.category && goalData.category !== 'general' ? 
                  (goalData.category.startsWith('cat_') ? goalData.category : `cat_${goalData.category.toLowerCase().replace(/\s+/g, '_')}`) : 
                  null),
    listId: goalData.listId || null,
    notify: notificationRules.enabled,
    notificationRules,
    icon: theme.icon,
    color: theme.color,
    theming: theme,
    progressSnapshots: Array.isArray(goalData.progressSnapshots)
      ? goalData.progressSnapshots
      : [],
    contributions: Array.isArray(goalData.contributions) ? goalData.contributions : [],
    // FINLIST ENVELOPE SYSTEM: Link to Budget envelope (Block B)
    // Goals MUST reference a valid envelopeId from Budget page envelopes
    linkedEnvelopeId: goalData.linkedEnvelopeId || null,
    // FINLIST CFO MODEL: Categories to track for automatic transaction sync
    // LIMIT goals use this to sum spending from matching categories
    trackingCategories: Array.isArray(goalData.trackingCategories) ? goalData.trackingCategories : [],
    trackAllExpenses: goalData.trackAllExpenses || false,
    linkedEnvelopeType: goalData.linkedEnvelopeType || null,
    // Keep goalId for internal reference
    goalId: goalData.goalId || null,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
};

const formatGoalUpdates = (updates = {}) => {
  const payload = { updatedAt: Timestamp.now() };

  if (Object.prototype.hasOwnProperty.call(updates, 'name')) {
    payload.name = (updates.name || '').trim();
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'description')) {
    payload.description = updates.description || '';
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'targetAmount') || Object.prototype.hasOwnProperty.call(updates, 'target')) {
    const targetAmount = Math.max(0, parseFloat(updates.targetAmount ?? updates.target) || 0);
    payload.targetAmount = targetAmount;
    payload.target = targetAmount;
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'currentAmount')) {
    payload.currentAmount = Math.max(0, parseFloat(updates.currentAmount) || 0);
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'monthlyContribution')) {
    payload.monthlyContribution = Math.max(0, parseFloat(updates.monthlyContribution) || 0);
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'plannedPerPeriod')) {
    payload.plannedPerPeriod = Math.max(0, parseFloat(updates.plannedPerPeriod) || 0);
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'cadence')) {
    const c = (updates.cadence || '').toUpperCase();
    payload.cadence = ['WEEKLY','MONTHLY','ONGOING'].includes(c) ? c : 'ONGOING';
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'dueDate')) {
    payload.dueDate = toTimestamp(updates.dueDate);
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'intent') || Object.prototype.hasOwnProperty.call(updates, 'goalType')) {
    const intent = normalizeIntent(updates.intent || updates.goalType);
    payload.intent = intent;
    payload.goalType = intent;
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'goalMode')) {
    const targetIntent = payload.intent || updates.intent || updates.goalType;
    payload.goalMode = normalizeGoalMode(updates.goalMode, targetIntent);
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'timeframe')) {
    payload.timeframe = normalizeTimeframe(updates.timeframe);
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'timeframeLabel')) {
    payload.timeframeLabel = updates.timeframeLabel;
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'timeframeStart')) {
    payload.timeframeStart = toTimestamp(updates.timeframeStart);
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'timeframeEnd')) {
    payload.timeframeEnd = toTimestamp(updates.timeframeEnd);
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'status')) {
    payload.status = updates.status;
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'priority')) {
    payload.priority = normalizePriority(updates.priority);
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'category')) {
    payload.category = updates.category || 'general';
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'categoryGroup')) {
    payload.categoryGroup = updates.categoryGroup || updates.category || 'general';
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'categorySub')) {
    payload.categorySub = updates.categorySub || null;
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'listId')) {
    payload.listId = updates.listId || null;
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'notify')) {
    payload.notify = Boolean(updates.notify);
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'notificationRules')) {
    const normalized = normalizeNotificationRules(updates.notificationRules, updates.notify);
    payload.notificationRules = normalized;
    payload.notify = normalized.enabled;
  }
  if (
    Object.prototype.hasOwnProperty.call(updates, 'icon') ||
    Object.prototype.hasOwnProperty.call(updates, 'color') ||
    Object.prototype.hasOwnProperty.call(updates, 'theming')
  ) {
    const theme = normalizeTheme({
      icon: updates.theming?.icon ?? updates.icon,
      color: updates.theming?.color ?? updates.color,
    });
    payload.icon = theme.icon;
    payload.color = theme.color;
    payload.theming = theme;
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'progressSnapshots')) {
    payload.progressSnapshots = Array.isArray(updates.progressSnapshots)
      ? updates.progressSnapshots
      : [];
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'contributions')) {
    payload.contributions = Array.isArray(updates.contributions)
      ? updates.contributions
      : [];
    // Update lastContributionAt if we have at least one contribution with a date
    const latest = payload.contributions
      .map(c => c.date || c.at || c.timestamp)
      .filter(Boolean)
      .map(d => (d?.toDate ? d.toDate() : new Date(d)))
      .sort((a,b)=>b-a)[0];
    if (latest) {
      payload.lastContributionAt = toTimestamp(latest);
    }
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'lastContributionAt')) {
    payload.lastContributionAt = toTimestamp(updates.lastContributionAt);
  }
  // FINLIST ENVELOPE SYSTEM: Allow updating linkedEnvelopeId
  if (Object.prototype.hasOwnProperty.call(updates, 'linkedEnvelopeId')) {
    payload.linkedEnvelopeId = updates.linkedEnvelopeId || null;
  }
  // Allow updating goalId for internal reference
  if (Object.prototype.hasOwnProperty.call(updates, 'goalId')) {
    payload.goalId = updates.goalId || null;
  }

  return payload;
};

const mapGoalSnapshot = (docSnap) => {
  const data = docSnap.data();
  const intent = normalizeIntent(data.intent || data.goalType);
  const goalMode = normalizeGoalMode(data.goalMode, intent);
  const theme = normalizeTheme(data.theming || { icon: data.icon, color: data.color });
  const notificationRules = normalizeNotificationRules(data.notificationRules, data.notify);

  return {
    id: docSnap.id,
    name: data.name,
    description: data.description || '',
    targetAmount: data.targetAmount ?? data.target ?? 0,
    target: data.targetAmount ?? data.target ?? 0,
    currentAmount: data.currentAmount ?? 0,
    monthlyContribution: data.monthlyContribution ?? 0,
    dueDate: fromTimestamp(data.dueDate),
    icon: theme.icon,
    color: theme.color,
    theming: theme,
    intent,
    goalType: intent,
    goalMode,
    timeframe: normalizeTimeframe(data.timeframe),
    timeframeLabel: data.timeframeLabel || 'Ongoing goal',
    timeframeStart: fromTimestamp(data.timeframeStart),
    timeframeEnd: fromTimestamp(data.timeframeEnd),
    status: data.status || GOAL_STATUS.ACTIVE,
    priority: normalizePriority(data.priority),
    category: data.category || data.categoryGroup || 'general',
    categoryGroup: data.categoryGroup || data.category || 'general',
    categorySub: data.categorySub || null,
    listId: data.listId || null,
    notify: notificationRules.enabled,
    notificationRules,
    createdAt: fromTimestamp(data.createdAt),
    updatedAt: fromTimestamp(data.updatedAt),
    progressSnapshots: Array.isArray(data.progressSnapshots)
      ? data.progressSnapshots.map((entry) => ({
          ...entry,
          at: fromTimestamp(entry.at),
        }))
      : [],
    contributions: Array.isArray(data.contributions)
      ? data.contributions.map((entry) => ({
          ...entry,
          date: fromTimestamp(entry.date),
        }))
      : [],
    cadence: data.cadence || 'ONGOING',
    plannedPerPeriod: typeof data.plannedPerPeriod === 'number' ? data.plannedPerPeriod : (data.monthlyContribution ?? 0),
    lastContributionAt: fromTimestamp(data.lastContributionAt),
    // FINLIST ENVELOPE SYSTEM: Include linkedEnvelopeId in goal data
    linkedEnvelopeId: data.linkedEnvelopeId || null,
    goalId: data.goalId || docSnap.id,
  };
};

/**
 * Create a new savings goal
 */
export async function createGoal(userId, goalData) {
  try {
    const goalPath = savingsGoalsPath(userId);
    const goalsRef = collection(db, goalPath);
    const goalPayload = formatGoalForCreate(goalData);
    
    // DEV-ONLY: Enhanced debug logging for permission issues
    if (import.meta.env.DEV) {
      console.log('🎯 [GOALS] createGoal invoked', {
        userId: userId?.substring(0, 12) + '...',
        fullPath: goalPath,
        auth: {
          currentUser: auth.currentUser ? 'present' : 'MISSING',
          uid: auth.currentUser?.uid?.substring(0, 12) + '...',
          uidMatch: auth.currentUser?.uid === userId
        },
        payload: {
          name: goalPayload.name,
          targetAmount: goalPayload.targetAmount,
          intent: goalPayload.intent,
          goalMode: goalPayload.goalMode
        }
      });
    }
    
    const docRef = await addDoc(goalsRef, goalPayload);
    const snapshot = await getDoc(docRef);
    const goal = mapGoalSnapshot(snapshot);
    emitGoalAnalyticsEvent('goal_created', {
      goalId: docRef.id,
      intent: goal.intent,
      mode: goal.goalMode,
      timeframe: goal.timeframe,
      priority: goal.priority,
      hasDueDate: Boolean(goal.dueDate),
      notify: Boolean(goal.notify),
      targetAmount: Number(goal.targetAmount || 0),
    });
    
    if (import.meta.env.DEV) {
      console.log('✅ [GOALS] Goal created successfully', { goalId: docRef.id });
    }
    
    return goal;
  } catch (error) {
    // Enhanced error logging with full context
    console.error('❌ [PERM] goals.create permission-denied', {
      path: savingsGoalsPath(userId),
      userId: userId?.substring(0, 12) + '...',
      authPresent: !!auth.currentUser,
      authUid: auth.currentUser?.uid?.substring(0, 12) + '...',
      errorCode: error.code,
      errorMessage: error.message,
      fullError: error
    });
    
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: Check Firestore security rules or authentication status');
    }
    throw error;
  }
}

/**
 * Get all goals for a user
 */
export async function getGoals(userId, options = {}) {
  if (!userId) {
    throw new Error('User ID is required to fetch goals.');
  }
  try {
    // Primary: savingsGoals collection (preferred)
    const goalsRef = collection(db, savingsGoalsPath(userId));
    let q = query(goalsRef);

    // Filter by status
    if (options.status) {
      q = query(goalsRef, where('status', '==', options.status));
    }

    // Order by
    const orderByField = options.orderBy || 'createdAt';
    const orderDirection = options.orderDirection || 'desc';
    q = query(q, orderBy(orderByField, orderDirection));

    const snapshot = await getDocs(q);
    const goals = snapshot.docs.map((docSnap) => mapGoalSnapshot(docSnap));

    // Update status based on due date and completion
    const now = new Date();
    const updatedGoals = goals.map((goal) => {
      let status = goal.status;
      const isLimitGoal = goal.goalMode === GOAL_MODE.LIMIT;

      // Check if goal is completed
      if (
        !isLimitGoal &&
        goal.currentAmount >= goal.targetAmount &&
        status !== GOAL_STATUS.COMPLETED
      ) {
        status = GOAL_STATUS.COMPLETED;
      }
      // Limit goals breach handling
      else if (
        isLimitGoal &&
        goal.currentAmount > goal.targetAmount &&
        status !== GOAL_STATUS.OVERDUE
      ) {
        status = GOAL_STATUS.OVERDUE;
      }
      // Check if goal is overdue
      else if (goal.dueDate && isAfter(now, goal.dueDate) && status === GOAL_STATUS.ACTIVE) {
        status = GOAL_STATUS.OVERDUE;
      }

      return { ...goal, status };
    });

    return updatedGoals;
  } catch (error) {
    console.error('Error getting goals (savingsGoals):', error);
    throw error;
  }
}

/**
 * Get a single goal by ID
 */
export async function getGoalById(userId, goalId) {
  try {
    const goalRef = doc(db, savingsGoalsPath(userId), goalId);
    const goalDoc = await getDoc(goalRef);

    if (!goalDoc.exists()) {
      throw new Error('Goal not found');
    }

    return mapGoalSnapshot(goalDoc);
  } catch (error) {
    console.error('Error getting goal:', error);
    throw error;
  }
}

/**
 * Find a goal by linked envelope ID or matching name
 * Used to connect budget savings allocations to existing goals
 * @param {string} userId 
 * @param {object} criteria - { linkedEnvelopeId, categoryId, name }
 * @returns {Promise<Object|null>} Goal or null if not found
 */
export async function findGoalByLinkedEnvelope(userId, criteria = {}) {
  try {
    const goals = await getGoals(userId);
    
    // Priority 1: Find by linkedEnvelopeId (direct link)
    if (criteria.linkedEnvelopeId) {
      const linkedGoal = goals.find(g => g.linkedEnvelopeId === criteria.linkedEnvelopeId);
      if (linkedGoal) return linkedGoal;
    }
    
    // Priority 2: Find by matching name (case-insensitive)
    if (criteria.name) {
      const normalizedName = criteria.name.toLowerCase().trim();
      const namedGoal = goals.find(g => 
        g.name.toLowerCase().trim() === normalizedName ||
        g.name.toLowerCase().includes(normalizedName) ||
        normalizedName.includes(g.name.toLowerCase())
      );
      if (namedGoal) return namedGoal;
    }
    
    // Priority 3: Find by category (emergency-fund, debt-repayment, etc.)
    if (criteria.categoryId) {
      const categoryGoal = goals.find(g => 
        g.category === criteria.categoryId || 
        g.categoryGroup === criteria.categoryId ||
        g.intent === criteria.categoryId.replace('-', '_')
      );
      if (categoryGoal) return categoryGoal;
    }
    
    // Priority 4: For generic savings envelopes, find the first ACCUMULATE savings goal
    // This handles cases where envelope is named "Savings" but goal is "Emergency Fund"
    const isSavingsEnvelope = criteria.categoryId && (
      criteria.categoryId.toLowerCase().includes('savings') ||
      criteria.categoryId.toLowerCase().includes('emergency') ||
      criteria.categoryId === 'cat_savings'
    );
    const isSavingsNamedEnvelope = criteria.name && (
      criteria.name.toLowerCase() === 'savings' ||
      criteria.name.toLowerCase().includes('saving')
    );
    
    if (isSavingsEnvelope || isSavingsNamedEnvelope) {
      // Look for any active savings goal (ACCUMULATE mode, not LIMIT)
      const savingsGoal = goals.find(g => 
        (g.goalMode === 'accumulate' || !g.goalMode) && // ACCUMULATE is default for savings
        g.status !== 'COMPLETED' &&
        g.status !== 'CANCELLED' &&
        (g.intent === 'save' || 
         g.intent === 'emergency_fund' ||
         g.category?.toLowerCase().includes('savings') ||
         g.category?.toLowerCase().includes('emergency') ||
         g.name?.toLowerCase().includes('savings') ||
         g.name?.toLowerCase().includes('emergency'))
      );
      if (savingsGoal) {
        console.log('[GOALS SERVICE] Matched generic savings envelope to goal:', savingsGoal.name);
        return savingsGoal;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error finding goal by envelope:', error);
    return null;
  }
}

/**
 * FINLIST CFO MODEL: Sync LIMIT goals with transaction spending
 * 
 * LIMIT goals (spending caps) track spending against a category.
 * This function calculates total spending from transactions and updates
 * the goal's currentAmount to reflect actual spending.
 * 
 * Uses the goal's trackingCategories field for precise matching.
 * 
 * @param {string} userId 
 * @param {string} goalId - Optional: sync specific goal, or all LIMIT goals if null
 * @returns {Promise<Object>} { synced: number, goals: Array }
 */
export async function syncLimitGoalsWithTransactions(userId, goalId = null) {
  try {
    console.log('[GOALS SERVICE] Syncing LIMIT goals with transactions...');
    
    // Get all LIMIT goals (or specific goal)
    const allGoals = await getGoals(userId);
    const limitGoals = allGoals.filter(g => {
      const isLimit = g.goalMode === GOAL_MODE.LIMIT;
      const matchesId = goalId ? g.id === goalId : true;
      return isLimit && matchesId;
    });
    
    if (limitGoals.length === 0) {
      console.log('[GOALS SERVICE] No LIMIT goals found to sync');
      return { synced: 0, goals: [] };
    }
    
    // Get transactions based on goal timeframe
    const transactionsRef = collection(db, `users/${userId}/transactions`);
    const now = new Date();
    
    // Update each LIMIT goal with spending from matching categories
    const syncedGoals = [];
    
    for (const goal of limitGoals) {
      // Calculate date range based on goal timeframe
      let startDate, endDate;
      if (goal.timeframe === 'weekly') {
        // Get start of current week (Monday)
        const dayOfWeek = now.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 6);
        endDate.setHours(23, 59, 59);
      } else {
        // Monthly (default)
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      }
      
      // Build tracking categories list
      const goalCategories = [];
      
      // Priority 1: Use explicit trackingCategories from template
      if (Array.isArray(goal.trackingCategories) && goal.trackingCategories.length > 0) {
        goal.trackingCategories.forEach(cat => {
          if (cat !== '*') goalCategories.push(cat.toLowerCase());
        });
      }
      
      // Priority 2: Use category fields as fallback
      if (goalCategories.length === 0) {
        if (goal.category) goalCategories.push(goal.category.toLowerCase());
        if (goal.categoryGroup && goal.categoryGroup !== goal.category) {
          goalCategories.push(goal.categoryGroup.toLowerCase());
        }
        if (goal.categorySub) goalCategories.push(goal.categorySub.toLowerCase());
        
        // Extract category hints from goal name
        const nameLower = goal.name.toLowerCase();
        const categoryKeywords = ['groceries', 'food', 'dining', 'transport', 'entertainment', 'shopping', 'utilities', 'health', 'restaurant'];
        for (const keyword of categoryKeywords) {
          if (nameLower.includes(keyword) && !goalCategories.includes(keyword)) {
            goalCategories.push(keyword);
          }
        }
      }
      
      // Check if this is a "track all expenses" goal
      const trackAll = goal.trackAllExpenses || 
                       (Array.isArray(goal.trackingCategories) && goal.trackingCategories.includes('*'));
      
      console.log('[GOALS SERVICE] Goal:', goal.name, 
                  'timeframe:', goal.timeframe,
                  'trackAll:', trackAll,
                  'tracking categories:', goalCategories);
      
      // Query transactions for this period
      const txQuery = query(
        transactionsRef,
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(endDate))
      );
      
      const txSnapshot = await getDocs(txQuery);
      const transactions = txSnapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));
      
      // Sum transactions matching the goal's categories
      let totalSpending = 0;
      const matchedTxIds = [];
      
      for (const tx of transactions) {
        // Skip non-expense transactions
        if (tx.type === 'income' || tx.type === 'savings') continue;
        if (tx.isRefund) continue;
        
        // Track all expenses?
        if (trackAll) {
          totalSpending += Math.abs(tx.amount || 0);
          matchedTxIds.push(tx.id);
          continue;
        }
        
        // Get transaction category info
        const txCategory = (tx.categoryId || tx.category || '').toLowerCase();
        const txNote = (tx.note || tx.description || '').toLowerCase();
        const txMerchant = (tx.merchant || '').toLowerCase();
        
        // Check if transaction matches any goal category
        const matches = goalCategories.some(cat => {
          // Exact match
          if (txCategory === cat) return true;
          // Partial match (category contains or is contained by)
          if (txCategory.includes(cat) || cat.includes(txCategory)) return true;
          // Check note/description
          if (txNote.includes(cat)) return true;
          // Check merchant name
          if (txMerchant.includes(cat)) return true;
          return false;
        });
        
        if (matches) {
          totalSpending += Math.abs(tx.amount || 0);
          matchedTxIds.push(tx.id);
        }
      }
      
      console.log('[GOALS SERVICE] Goal:', goal.name, 
                  'total spending:', totalSpending.toFixed(2), 
                  'from', matchedTxIds.length, 'transactions');
      
      // Update goal's currentAmount if changed
      if (Math.abs(goal.currentAmount - totalSpending) > 0.01) {
        const goalRef = doc(db, savingsGoalsPath(userId), goal.id);
        
        // Determine new status
        let newStatus = goal.status;
        if (totalSpending > goal.targetAmount) {
          newStatus = GOAL_STATUS.OVERDUE; // Over limit
        } else if (totalSpending <= goal.targetAmount && goal.status === GOAL_STATUS.OVERDUE) {
          newStatus = GOAL_STATUS.ACTIVE; // Back under limit
        }
        
        await updateDoc(goalRef, {
          currentAmount: totalSpending,
          current: totalSpending,
          status: newStatus,
          lastSyncedAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
        
        syncedGoals.push({
          id: goal.id,
          name: goal.name,
          previousAmount: goal.currentAmount,
          newAmount: totalSpending,
          transactionCount: matchedTxIds.length,
          status: newStatus
        });
      }
    }
    
    console.log('[GOALS SERVICE] Synced', syncedGoals.length, 'LIMIT goals');
    return { synced: syncedGoals.length, goals: syncedGoals };
    
  } catch (error) {
    console.error('[GOALS SERVICE] Error syncing LIMIT goals:', error);
    throw error;
  }
}

/**
 * Find LIMIT goals that track a specific category
 * @param {string} userId 
 * @param {string} categoryId 
 * @returns {Promise<Array>} Matching LIMIT goals
 */
export async function findLimitGoalsByCategory(userId, categoryId) {
  try {
    const allGoals = await getGoals(userId);
    const categoryLower = categoryId.toLowerCase();
    
    return allGoals.filter(goal => {
      if (goal.goalMode !== GOAL_MODE.LIMIT) return false;
      
      // Check all category fields
      const goalCategory = (goal.category || '').toLowerCase();
      const goalCategoryGroup = (goal.categoryGroup || '').toLowerCase();
      const goalCategorySub = (goal.categorySub || '').toLowerCase();
      const goalName = (goal.name || '').toLowerCase();
      
      return goalCategory.includes(categoryLower) ||
             categoryLower.includes(goalCategory) ||
             goalCategoryGroup.includes(categoryLower) ||
             goalCategorySub.includes(categoryLower) ||
             goalName.includes(categoryLower);
    });
  } catch (error) {
    console.error('[GOALS SERVICE] Error finding LIMIT goals by category:', error);
    return [];
  }
}

/**
 * Update a goal
 */
export async function updateGoal(userId, goalId, updates) {
  try {
    const goalRef = doc(db, savingsGoalsPath(userId), goalId);

    const updateData = formatGoalUpdates(updates);
    await updateDoc(goalRef, updateData);
    const updatedGoal = await getGoalById(userId, goalId);
    const changedFields = Object.keys(updateData).filter((field) => field !== 'updatedAt');
    emitGoalAnalyticsEvent('goal_updated', {
      goalId,
      changed: changedFields.slice(0, 8).join(',') || 'metadata',
      status: updatedGoal.status,
      mode: updatedGoal.goalMode,
      notify: Boolean(updatedGoal.notify),
    });

    return updatedGoal;
  } catch (error) {
    console.error('Error updating goal:', error);
    throw error;
  }
}

/**
 * Delete a goal
 */
export async function deleteGoal(userId, goalId) {
  try {
    const goalRef = doc(db, savingsGoalsPath(userId), goalId);
    await deleteDoc(goalRef);
    emitGoalAnalyticsEvent('goal_deleted', { goalId });
  } catch (error) {
    console.error('Error deleting goal:', error);
    throw error;
  }
}

/**
 * Add contribution to a goal
 * FINLIST CFO MODEL: Bi-directional sync with budget envelopes
 * - When contributing from Goals page → also updates linked budget envelope
 * - When funding from Budget page → options.fromBudget = true (skip reverse sync)
 * 
 * @param {string} userId 
 * @param {string} goalId 
 * @param {number} amount 
 * @param {string} note 
 * @param {object} options - { fromBudget: boolean, budgetId, lineId, skipBudgetSync }
 */
export async function addContribution(userId, goalId, amount, note = '', options = {}) {
  try {
    const goalRef = doc(db, savingsGoalsPath(userId), goalId);
    const goal = await getGoalById(userId, goalId);

    const contribution = {
      amount: parseFloat(amount),
      note,
      date: Timestamp.now(),
      // Track source if from budget funding
      source: options.fromBudget ? 'budget-funding' : 'manual',
      budgetId: options.budgetId || null,
    };

    // Update goal with new contribution
    const newCurrentAmount = goal.currentAmount + parseFloat(amount);
    const isLimitGoal = goal.goalMode === GOAL_MODE.LIMIT;
    let newStatus = goal.status;

    if (!isLimitGoal && newCurrentAmount >= goal.targetAmount) {
      newStatus = GOAL_STATUS.COMPLETED;
    } else if (isLimitGoal && newCurrentAmount > goal.targetAmount) {
      newStatus = GOAL_STATUS.OVERDUE;
    }

    await updateDoc(goalRef, {
      currentAmount: newCurrentAmount,
      status: newStatus,
      contributions: [...(goal.contributions || []), contribution],
      updatedAt: Timestamp.now(),
    });

    // FINLIST CFO MODEL: Bi-directional sync with budget envelope
    // If contributing from Goals page (not from Budget), sync to budget envelope
    let budgetSyncResult = null;
    if (!options.fromBudget && !options.skipBudgetSync) {
      try {
        // Dynamic import to avoid circular dependencies (only for budgetService)
        const { getActiveBudget, getSavingsEnvelopes } = await import('./budgetService.js');
        
        // Find the active budget
        const activeBudget = await getActiveBudget(userId);
        if (activeBudget?.id) {
          // Find a matching savings envelope by goal name or linkedEnvelopeId
          const savingsEnvelopes = await getSavingsEnvelopes(userId, activeBudget.id);
          console.log('[GOALS SERVICE] Looking for match. Goal:', goal.name, 'Savings envelopes:', savingsEnvelopes.map(e => e.categoryName || e.name));
          
          const matchingLine = savingsEnvelopes.find(line => {
            // Check if this budget line matches the goal
            const lineNameLower = (line.categoryName || line.name || '').toLowerCase();
            const goalNameLower = goal.name.toLowerCase();
            return (
              line.linkedGoalId === goalId ||
              line.id === goal.linkedEnvelopeId ||
              lineNameLower === goalNameLower ||
              lineNameLower.includes(goalNameLower) ||
              goalNameLower.includes(lineNameLower)
            );
          });
          
          if (matchingLine) {
            // Update the budget envelope's fundedThisMonth
            // Use already imported 'doc', 'updateDoc', 'Timestamp', and 'db'
            const lineRef = doc(db, 'users', userId, 'budgets', activeBudget.id, 'lines', matchingLine.id);
            const currentFunded = matchingLine.fundedThisMonth || 0;
            const newFunded = currentFunded + parseFloat(amount);
            
            await updateDoc(lineRef, {
              fundedThisMonth: newFunded,
              actual: newFunded,
              remaining: Math.max(0, (matchingLine.planned || 0) - newFunded),
              updatedAt: Timestamp.now()
            });
            
            budgetSyncResult = {
              synced: true,
              budgetId: activeBudget.id || null,
              lineId: matchingLine.id || null,
              lineName: matchingLine.categoryName || matchingLine.name || 'Savings',
              newFunded: newFunded || 0
            };
            
            console.log('[GOALS SERVICE] Synced contribution to budget envelope:', budgetSyncResult);
          } else {
            budgetSyncResult = { synced: false, reason: 'No matching budget envelope found' };
            console.log('[GOALS SERVICE] No matching budget envelope for goal:', goal.name);
          }
        } else {
          budgetSyncResult = { synced: false, reason: 'No active budget' };
        }
      } catch (budgetError) {
        console.warn('[GOALS SERVICE] Budget sync failed (non-blocking):', budgetError.message);
        budgetSyncResult = { synced: false, reason: budgetError.message };
      }
    }

    // Create transaction for savings engine to track
    // CRITICAL: Only ACCUMULATE goals create 'savings' type transactions
    // LIMIT goals (spending caps) should create 'expense' type to track spending against cap
    try {
      const transactionsRef = collection(db, `users/${userId}/transactions`);
      const isAccumulateGoal = !isLimitGoal; // Already computed above
      
      // Derive proper categoryId for LIMIT goals from multiple sources
      // Priority: categoryId > category > categoryGroup > first trackingCategory > fallback
      const deriveCategoryId = () => {
        if (isAccumulateGoal) return 'cat_savings';
        if (goal.categoryId) return goal.categoryId;
        if (goal.category && goal.category !== 'general') {
          // Convert category name to cat_xxx format if needed
          const cat = goal.category.toLowerCase().replace(/\s+/g, '_');
          return cat.startsWith('cat_') ? cat : `cat_${cat}`;
        }
        if (goal.categoryGroup && goal.categoryGroup !== 'general' && goal.categoryGroup !== 'spending') {
          const catGroup = goal.categoryGroup.toLowerCase().replace(/\s+/g, '_');
          return catGroup.startsWith('cat_') ? catGroup : `cat_${catGroup}`;
        }
        // Use first trackingCategory that looks like a proper category
        if (Array.isArray(goal.trackingCategories) && goal.trackingCategories.length > 0) {
          const firstCat = goal.trackingCategories[0];
          if (firstCat && firstCat !== '*') {
            const cat = firstCat.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
            return cat.startsWith('cat_') ? cat : `cat_${cat}`;
          }
        }
        return 'cat_general';
      };
      
      // Sanitize budgetSyncResult to remove any undefined values (Firestore rejects undefined)
      const safeBudgetSync = budgetSyncResult ? {
        synced: budgetSyncResult.synced || false,
        budgetId: budgetSyncResult.budgetId || null,
        lineId: budgetSyncResult.lineId || null,
        lineName: budgetSyncResult.lineName || null,
        newFunded: budgetSyncResult.newFunded || 0,
        reason: budgetSyncResult.reason || null,
      } : { synced: false, reason: 'No sync attempted' };
      
      await addDoc(transactionsRef, {
        type: isAccumulateGoal ? 'savings' : 'expense',
        amount: parseFloat(amount),
        description: note || `${isAccumulateGoal ? 'Contribution to' : 'Spending on'} ${goal.name}`,
        goalId: goalId,
        categoryId: deriveCategoryId(),
        date: Timestamp.now(),
        createdAt: Timestamp.now(),
        meta: {
          isGoalContribution: isAccumulateGoal,
          isLimitSpending: isLimitGoal,
          goalName: goal.name || 'Unknown Goal',
          goalMode: goal.goalMode || 'accumulate',
          budgetSync: safeBudgetSync,
        },
      });
    } catch (txError) {
      // Transaction creation is non-blocking - log but don't fail the contribution
      console.warn('[GOALS SERVICE] Transaction creation failed (non-blocking):', txError.message);
    }

    const updatedGoal = await getGoalById(userId, goalId);
    emitGoalAnalyticsEvent('goal_contribution_added', {
      goalId,
      amount: Number(contribution.amount) || 0,
      status: updatedGoal.status,
      mode: updatedGoal.goalMode,
      completed: updatedGoal.status === GOAL_STATUS.COMPLETED,
      budgetSynced: budgetSyncResult?.synced || false,
    });

    // Return goal with budget sync info
    return {
      ...updatedGoal,
      budgetSync: budgetSyncResult
    };
  } catch (error) {
    console.error('Error adding contribution:', error);
    throw error;
  }
}

/**
 * Withdraw from a goal
 */
export async function withdrawFromGoal(userId, goalId, amount, note = '') {
  try {
    const goalRef = doc(db, savingsGoalsPath(userId), goalId);
    const goal = await getGoalById(userId, goalId);

    const withdrawal = {
      amount: -parseFloat(amount),
      note: note || 'Withdrawal',
      date: Timestamp.now(),
    };

    const newCurrentAmount = Math.max(0, goal.currentAmount - parseFloat(amount));

    await updateDoc(goalRef, {
      currentAmount: newCurrentAmount,
      status: GOAL_STATUS.ACTIVE,
      contributions: [...(goal.contributions || []), withdrawal],
      updatedAt: Timestamp.now(),
    });

    // Create transaction for savings engine to track (negative amount)
    try {
      const transactionsRef = collection(db, `users/${userId}/transactions`);
      await addDoc(transactionsRef, {
        type: 'savings',
        amount: parseFloat(amount), // Positive in transaction, but marked as withdrawal
        description: note || `Withdrawal from ${goal.name || 'Goal'}`,
        goalId: goalId,
        categoryId: 'cat_savings',
        date: Timestamp.now(),
        createdAt: Timestamp.now(),
        meta: {
          isWithdrawal: true,
          isGoalContribution: false,
          goalName: goal.name || 'Unknown Goal',
          goalMode: goal.goalMode || 'accumulate',
        },
      });
    } catch (txError) {
      // Transaction creation is non-blocking
      console.warn('[GOALS SERVICE] Transaction creation failed (non-blocking):', txError.message);
    }

    const updatedGoal = await getGoalById(userId, goalId);
    emitGoalAnalyticsEvent('goal_withdrawal_made', {
      goalId,
      amount: Math.abs(Number(withdrawal.amount) || 0),
      status: updatedGoal.status,
      mode: updatedGoal.goalMode,
    });

    return updatedGoal;
  } catch (error) {
    console.error('Error withdrawing from goal:', error);
    throw error;
  }
}

/**
 * Calculate goal statistics
 */
export function calculateGoalStats(goal) {
  const mode = goal.goalMode || GOAL_MODE.ACCUMULATE;
  const rawProgress = goal.targetAmount > 0 ? (goal.currentAmount / goal.targetAmount) * 100 : 0;
  const progress = Math.min(100, rawProgress);
  const remaining = Math.max(0, goal.targetAmount - goal.currentAmount);
  const isCompleted = mode === GOAL_MODE.ACCUMULATE && rawProgress >= 100;
  const limitBreached = mode === GOAL_MODE.LIMIT && goal.currentAmount >= goal.targetAmount;

  let monthsRemaining = null;
  let projectedCompletion = null;

  if (goal.dueDate) {
    monthsRemaining = differenceInMonths(goal.dueDate, new Date());
  }

  if (mode === GOAL_MODE.ACCUMULATE && goal.monthlyContribution > 0 && remaining > 0) {
    const monthsToComplete = Math.ceil(remaining / goal.monthlyContribution);
    projectedCompletion = new Date();
    projectedCompletion.setMonth(projectedCompletion.getMonth() + monthsToComplete);
  }

  const onTrack =
    mode === GOAL_MODE.LIMIT
      ? !limitBreached
      : goal.dueDate && projectedCompletion
      ? isBefore(projectedCompletion, goal.dueDate)
      : true;

  return {
    progress,
    remaining,
    isCompleted,
    monthsRemaining,
    projectedCompletion,
    onTrack,
    limitBreached,
    mode,
  };
}

/**
 * Get goals summary
 */
export async function getGoalsSummary(userId) {
  if (!userId) {
    throw new Error('User ID is required to summarize goals.');
  }
  try {
    const goals = await getGoals(userId);

    const totalGoals = goals.length;
    const activeGoals = goals.filter((g) => g.status === GOAL_STATUS.ACTIVE).length;
    const completedGoals = goals.filter((g) => g.status === GOAL_STATUS.COMPLETED).length;
    const overdueGoals = goals.filter((g) => g.status === GOAL_STATUS.OVERDUE).length;

    const totalTarget = goals.reduce((sum, g) => sum + g.targetAmount, 0);
    const totalSaved = goals.reduce((sum, g) => sum + g.currentAmount, 0);
    const totalRemaining = totalTarget - totalSaved;

    const monthlyCommitment = goals
      .filter((g) => g.status === GOAL_STATUS.ACTIVE)
      .reduce((sum, g) => sum + g.monthlyContribution, 0);

    return {
      totalGoals,
      activeGoals,
      completedGoals,
      overdueGoals,
      totalTarget,
      totalSaved,
      totalRemaining,
      monthlyCommitment,
      overallProgress: totalTarget > 0 ? (totalSaved / totalTarget) * 100 : 0,
    };
  } catch (error) {
    console.error('Error getting goals summary:', error);
    throw error;
  }
}

/**
 * Predefined goal templates
 * FINLIST CFO MODEL: Each template includes trackingCategories for automatic transaction sync
 * 
 * - ACCUMULATE goals: Link to savings envelopes for bi-directional funding
 * - LIMIT goals: Track spending from matching categories automatically
 * - DEBT goals: Track debt repayment progress
 */
export const GOAL_TEMPLATES = [
  // ============================================================================
  // SAVINGS GOALS (ACCUMULATE MODE) - Link to budget savings envelopes
  // ============================================================================
  {
    id: 'emergency-fund',
    name: 'Emergency Fund',
    description: '3-6 months of living expenses',
    icon: '🏥',
    color: '#EF476F',
    category: 'emergency',
    categoryGroup: 'savings',
    // FINLIST: Links to savings envelope for bi-directional sync
    linkedEnvelopeType: 'savings',
    trackingCategories: ['emergency', 'emergency-fund', 'cat_savings'],
    // FINLIST: Auto-match to budget envelope by these category keys (includes common envelope names)
    envelopeCategoryMatch: ['emergency-fund', 'emergency fund', 'emergency', 'savings-emergency', 'cat_savings'],
    suggestedTarget: 5000,
    goalType: 'save-money',
    intent: 'save-money',
    goalMode: GOAL_MODE.ACCUMULATE,
    timeframe: 'open',
    timeframeLabel: 'Ongoing safety net',
    priority: 'high',
    notificationRules: {
      channels: ['push', 'email'],
      thresholdPercent: 0.9,
    },
  },
  {
    id: 'vacation',
    name: 'Vacation',
    description: 'Dream holiday savings',
    icon: '✈️',
    color: '#118AB2',
    category: 'leisure',
    categoryGroup: 'savings',
    linkedEnvelopeType: 'savings',
    trackingCategories: ['vacation', 'travel', 'holiday'],
    // Matches: Leisure & Lifestyle, Vacation, Travel envelopes
    envelopeCategoryMatch: ['vacation', 'travel', 'holiday', 'leisure', 'leisure & lifestyle', 'leisure-lifestyle', 'leisure-travel'],
    suggestedTarget: 2000,
    goalType: 'save-money',
    intent: 'save-money',
    goalMode: GOAL_MODE.ACCUMULATE,
    timeframe: 'custom',
    timeframeLabel: 'Trip countdown',
    priority: 'medium',
    notificationRules: {
      channels: ['push'],
      thresholdPercent: 0.7,
    },
  },
  {
    id: 'house-deposit',
    name: 'House Deposit',
    description: 'Down payment for property',
    icon: '🏠',
    color: '#06D6A0',
    category: 'major',
    categoryGroup: 'savings',
    linkedEnvelopeType: 'savings',
    trackingCategories: ['house', 'property', 'deposit', 'housing'],
    // Matches: Housing & Rent, Housing, Mortgage envelopes
    envelopeCategoryMatch: ['house-deposit', 'housing', 'housing & rent', 'housing-rent', 'property', 'mortgage', 'rent'],
    suggestedTarget: 20000,
    goalType: 'save-money',
    intent: 'save-money',
    goalMode: GOAL_MODE.ACCUMULATE,
    timeframe: 'custom',
    timeframeLabel: 'Milestone driven',
    priority: 'high',
    notificationRules: {
      channels: ['push', 'email'],
      thresholdPercent: 0.85,
    },
  },
  {
    id: 'car',
    name: 'New Car',
    description: 'Vehicle purchase fund',
    icon: '🚗',
    color: '#FFD60A',
    category: 'major',
    categoryGroup: 'savings',
    linkedEnvelopeType: 'savings',
    trackingCategories: ['car', 'vehicle', 'auto', 'transportation'],
    // Matches: Transportation envelope
    envelopeCategoryMatch: ['car', 'vehicle', 'auto', 'transportation', 'transport', 'savings-car'],
    suggestedTarget: 10000,
    goalType: 'save-money',
    intent: 'save-money',
    goalMode: GOAL_MODE.ACCUMULATE,
    timeframe: 'custom',
    timeframeLabel: 'Milestone driven',
    priority: 'medium',
    notificationRules: {
      channels: ['push'],
      thresholdPercent: 0.75,
    },
  },
  {
    id: 'education',
    name: 'Education Fund',
    description: 'Course or degree funding',
    icon: '📚',
    color: '#8338EC',
    category: 'education',
    categoryGroup: 'savings',
    linkedEnvelopeType: 'savings',
    trackingCategories: ['education', 'school', 'course', 'training'],
    envelopeCategoryMatch: ['education', 'education fund', 'education-fund', 'school', 'training'],
    suggestedTarget: 5000,
    goalType: 'save-money',
    intent: 'save-money',
    goalMode: GOAL_MODE.ACCUMULATE,
    timeframe: 'quarterly',
    timeframeLabel: 'Term-based cadence',
    priority: 'medium',
    notificationRules: {
      channels: ['push'],
      thresholdPercent: 0.8,
    },
  },
  {
    id: 'wedding',
    name: 'Wedding',
    description: 'Big day savings',
    icon: '💍',
    color: '#FF006E',
    category: 'event',
    categoryGroup: 'savings',
    linkedEnvelopeType: 'savings',
    trackingCategories: ['wedding', 'event', 'celebration'],
    // Matches: Could map to Leisure & Lifestyle or a custom wedding envelope
    envelopeCategoryMatch: ['wedding', 'event', 'celebration', 'leisure', 'leisure & lifestyle'],
    suggestedTarget: 15000,
    goalType: 'save-money',
    intent: 'save-money',
    goalMode: GOAL_MODE.ACCUMULATE,
    timeframe: 'custom',
    timeframeLabel: 'Event deadline',
    priority: 'high',
    notificationRules: {
      channels: ['push', 'email'],
      thresholdPercent: 0.8,
    },
  },
  {
    id: 'retirement',
    name: 'Retirement',
    description: 'Long-term retirement fund',
    icon: '🏖️',
    color: '#06D6A0',
    category: 'retirement',
    categoryGroup: 'savings',
    linkedEnvelopeType: 'savings',
    trackingCategories: ['retirement', 'pension', 'investment'],
    envelopeCategoryMatch: ['retirement', 'pension', 'investment', 'savings-retirement'],
    suggestedTarget: 50000,
    goalType: 'save-money',
    intent: 'save-money',
    goalMode: GOAL_MODE.ACCUMULATE,
    timeframe: 'open',
    timeframeLabel: 'Lifetime horizon',
    priority: 'high',
    notificationRules: {
      channels: ['email'],
      thresholdPercent: 0.6,
    },
  },
  {
    id: 'gadget',
    name: 'New Gadget',
    description: 'Tech purchase savings',
    icon: '📱',
    color: '#3A86FF',
    category: 'tech',
    categoryGroup: 'savings',
    linkedEnvelopeType: 'savings',
    trackingCategories: ['gadget', 'electronics', 'tech', 'technology'],
    envelopeCategoryMatch: ['gadget', 'electronics', 'tech', 'technology', 'savings-tech'],
    suggestedTarget: 1000,
    goalType: 'save-money',
    intent: 'save-money',
    goalMode: GOAL_MODE.ACCUMULATE,
    timeframe: 'monthly',
    timeframeLabel: 'Monthly sprint',
    priority: 'low',
    notificationRules: {
      channels: ['push'],
      thresholdPercent: 0.65,
    },
  },
  
  // ============================================================================
  // SPENDING CAP GOALS (LIMIT MODE) - Track all expense transactions
  // ============================================================================
  {
    id: 'monthly-spending-cap',
    name: 'Monthly Spending Cap',
    description: 'Stay under a flexible limit',
    icon: '🚦',
    color: '#EF476F',
    category: 'general',
    categoryGroup: 'spending',
    // FINLIST: Tracks ALL expense categories - no specific envelope match
    trackingCategories: ['*'], // Wildcard = all expenses
    envelopeCategoryMatch: [], // No specific envelope - tracks all spending
    trackAllExpenses: true,
    suggestedTarget: 1500,
    goalType: 'spending-cap',
    intent: 'spending-cap',
    goalMode: GOAL_MODE.LIMIT,
    timeframe: 'monthly',
    timeframeLabel: 'Monthly reset',
    priority: 'high',
    notificationRules: {
      channels: ['push', 'email'],
      thresholdPercent: 0.85,
    },
  },
  {
    id: 'weekly-spending-limit',
    name: 'Weekly Spending Limit',
    description: 'Keep weekly spending in check',
    icon: '🚦',
    color: '#EF476F',
    category: 'general',
    categoryGroup: 'spending',
    trackingCategories: ['*'],
    envelopeCategoryMatch: [], // No specific envelope - tracks all spending
    trackAllExpenses: true,
    suggestedTarget: 350,
    goalType: 'spending-cap',
    intent: 'spending-cap',
    goalMode: GOAL_MODE.LIMIT,
    timeframe: 'weekly',
    timeframeLabel: 'Weekly reset',
    priority: 'medium',
    notificationRules: {
      channels: ['push'],
      thresholdPercent: 0.8,
    },
  },
  
  // ============================================================================
  // CATEGORY LIMIT GOALS (LIMIT MODE) - Track specific category spending
  // ============================================================================
  {
    id: 'groceries-limit',
    name: 'Groceries Limit',
    description: 'Keep grocery spending in check',
    icon: '🗂️',
    color: '#3A86FF',
    category: 'groceries',
    categoryGroup: 'spending',
    // FINLIST: Tracks groceries and food categories
    trackingCategories: ['groceries', 'groceries-food', 'food', 'grocery', 'cat_groceries', 'supermarket', 'market'],
    // Matches: "Groceries & Food" envelope
    envelopeCategoryMatch: ['groceries', 'groceries & food', 'groceries-food', 'food', 'grocery', 'supermarket'],
    suggestedTarget: 400,
    goalType: 'category-limit',
    intent: 'category-limit',
    goalMode: GOAL_MODE.LIMIT,
    timeframe: 'monthly',
    timeframeLabel: 'Monthly grocery budget',
    priority: 'high',
    notificationRules: {
      channels: ['push'],
      thresholdPercent: 0.8,
    },
  },
  {
    id: 'entertainment-limit',
    name: 'Entertainment Limit',
    description: 'Control entertainment expenses',
    icon: '🗂️',
    color: '#3A86FF',
    category: 'entertainment',
    categoryGroup: 'spending',
    // FINLIST: Tracks entertainment and leisure categories
    trackingCategories: ['entertainment', 'leisure', 'leisure-lifestyle', 'cat_entertainment', 'movies', 'games', 'streaming', 'subscriptions', 'hobbies'],
    // Matches: "Leisure & Lifestyle" or "Subscriptions" envelope
    envelopeCategoryMatch: ['entertainment', 'leisure', 'leisure & lifestyle', 'leisure-lifestyle', 'subscriptions', 'streaming'],
    suggestedTarget: 200,
    goalType: 'category-limit',
    intent: 'category-limit',
    goalMode: GOAL_MODE.LIMIT,
    timeframe: 'monthly',
    timeframeLabel: 'Monthly fun budget',
    priority: 'medium',
    notificationRules: {
      channels: ['push'],
      thresholdPercent: 0.75,
    },
  },
  {
    id: 'dining-limit',
    name: 'Dining Out Limit',
    description: 'Manage restaurant spending',
    icon: '🗂️',
    color: '#3A86FF',
    category: 'dining',
    categoryGroup: 'spending',
    // FINLIST: Tracks dining and restaurant categories
    trackingCategories: ['dining', 'dining-out', 'restaurant', 'restaurants', 'cat_dining', 'takeaway', 'takeout', 'fast-food', 'cafe', 'coffee'],
    // Matches: "Dining Out" envelope
    envelopeCategoryMatch: ['dining', 'dining out', 'dining-out', 'restaurant', 'restaurants', 'takeaway', 'cafe'],
    suggestedTarget: 300,
    goalType: 'category-limit',
    intent: 'category-limit',
    goalMode: GOAL_MODE.LIMIT,
    timeframe: 'monthly',
    timeframeLabel: 'Monthly dining budget',
    priority: 'medium',
    notificationRules: {
      channels: ['push'],
      thresholdPercent: 0.8,
    },
  },
  {
    id: 'transport-limit',
    name: 'Transport Limit',
    description: 'Control transportation costs',
    icon: '🗂️',
    color: '#3A86FF',
    category: 'transport',
    categoryGroup: 'spending',
    trackingCategories: ['transport', 'transportation', 'fuel', 'gas', 'petrol', 'car', 'uber', 'taxi', 'public-transport', 'bus', 'train'],
    // Matches: "Transportation" envelope
    envelopeCategoryMatch: ['transport', 'transportation', 'fuel', 'car', 'uber', 'taxi', 'public-transport'],
    suggestedTarget: 250,
    goalType: 'category-limit',
    intent: 'category-limit',
    goalMode: GOAL_MODE.LIMIT,
    timeframe: 'monthly',
    timeframeLabel: 'Monthly transport budget',
    priority: 'medium',
    notificationRules: {
      channels: ['push'],
      thresholdPercent: 0.8,
    },
  },
  {
    id: 'shopping-limit',
    name: 'Shopping Limit',
    description: 'Control discretionary shopping',
    icon: '🗂️',
    color: '#3A86FF',
    category: 'shopping',
    categoryGroup: 'spending',
    trackingCategories: ['shopping', 'retail', 'clothing', 'clothes', 'fashion', 'accessories', 'amazon', 'online-shopping'],
    envelopeCategoryMatch: ['shopping', 'retail', 'clothing', 'clothes', 'fashion', 'amazon'],
    suggestedTarget: 200,
    goalType: 'category-limit',
    intent: 'category-limit',
    goalMode: GOAL_MODE.LIMIT,
    timeframe: 'monthly',
    timeframeLabel: 'Monthly shopping budget',
    priority: 'medium',
    notificationRules: {
      channels: ['push'],
      thresholdPercent: 0.75,
    },
  },
  
  // ============================================================================
  // DEBT PAYDOWN GOALS (ACCUMULATE MODE) - Track debt repayment
  // ============================================================================
  {
    id: 'credit-card-payoff',
    name: 'Credit Card Payoff',
    description: 'Track credit card repayments',
    icon: '📉',
    color: '#F59E0B',
    category: 'debt',
    categoryGroup: 'debt',
    linkedEnvelopeType: 'debt',
    trackingCategories: ['credit-card', 'credit', 'card-payment', 'debt-repayment'],
    // Matches: "Debt Repayment" envelope
    envelopeCategoryMatch: ['credit-card', 'credit', 'debt', 'debt repayment', 'debt-repayment', 'card-payment'],
    suggestedTarget: 3000,
    goalType: 'debt-paydown',
    intent: 'debt-paydown',
    goalMode: GOAL_MODE.ACCUMULATE,
    timeframe: 'custom',
    timeframeLabel: 'Debt-free deadline',
    priority: 'high',
    notificationRules: {
      channels: ['push', 'email'],
      thresholdPercent: 0.9,
    },
  },
  {
    id: 'student-loan-paydown',
    name: 'Student Loan Paydown',
    description: 'Track student loan repayments & momentum',
    icon: '📉',
    color: '#F59E0B',
    category: 'debt',
    categoryGroup: 'debt',
    linkedEnvelopeType: 'debt',
    trackingCategories: ['student-loan', 'student', 'loan', 'education-loan'],
    // Matches: "Debt Repayment" envelope
    envelopeCategoryMatch: ['student-loan', 'student', 'loan', 'debt', 'debt repayment', 'debt-repayment'],
    suggestedTarget: 10000,
    goalType: 'debt-paydown',
    intent: 'debt-paydown',
    goalMode: GOAL_MODE.ACCUMULATE,
    timeframe: 'open',
    timeframeLabel: 'Ongoing paydown',
    priority: 'high',
    notificationRules: {
      channels: ['push', 'email'],
      thresholdPercent: 0.85,
    },
  },
  {
    id: 'personal-loan-repayment',
    name: 'Personal Loan Repayment',
    description: 'Pay off personal loan faster',
    icon: '📉',
    color: '#F59E0B',
    category: 'debt',
    categoryGroup: 'debt',
    linkedEnvelopeType: 'debt',
    trackingCategories: ['personal-loan', 'loan', 'debt-repayment', 'loan-payment'],
    // Matches: "Debt Repayment" envelope
    envelopeCategoryMatch: ['personal-loan', 'loan', 'debt', 'debt repayment', 'debt-repayment', 'loan-payment'],
    suggestedTarget: 5000,
    goalType: 'debt-paydown',
    intent: 'debt-paydown',
    goalMode: GOAL_MODE.ACCUMULATE,
    timeframe: 'custom',
    timeframeLabel: 'Target payoff date',
    priority: 'high',
    notificationRules: {
      channels: ['push'],
      thresholdPercent: 0.8,
    },
  },
  {
    id: 'mortgage-overpayment',
    name: 'Mortgage Overpayment',
    description: 'Extra payments to reduce mortgage',
    icon: '📉',
    color: '#F59E0B',
    category: 'debt',
    categoryGroup: 'debt',
    linkedEnvelopeType: 'debt',
    trackingCategories: ['mortgage', 'housing', 'home-loan', 'mortgage-payment'],
    envelopeCategoryMatch: ['mortgage', 'housing', 'home-loan', 'mortgage-payment', 'debt-mortgage'],
    suggestedTarget: 10000,
    goalType: 'debt-paydown',
    intent: 'debt-paydown',
    goalMode: GOAL_MODE.ACCUMULATE,
    timeframe: 'open',
    timeframeLabel: 'Ongoing overpayment',
    priority: 'medium',
    notificationRules: {
      channels: ['push'],
      thresholdPercent: 0.7,
    },
  },
];

// ============================================================================
// GOAL CATEGORY FILTERS
// ============================================================================
// Maps each goal template to its relevant categories for contextual filtering
// in the Set Goal page category dropdown
//
// categoryType: 'savings' | 'expense' | 'all'
// hideDropdown: true → hide category selector entirely (for ALL expenses goals)
// autoSelectCategory: category ID to auto-select when template is chosen
// allowedCategories: array of allowed category IDs (for filtering)
// infoMessage: message to show when dropdown is hidden
// ============================================================================

export const GOAL_CATEGORY_FILTERS = {
  // ============================================================================
  // SAVINGS GOALS (ACCUMULATE) - Filter to relevant savings categories
  // ============================================================================
  'emergency-fund': {
    categoryType: 'savings',
    hideDropdown: false,
    autoSelectCategory: 'emergency-fund',
    allowedCategories: ['emergency-fund'],
    keywords: ['emergency', 'emergency-fund', 'rainy-day', 'safety-net']
  },
  'vacation': {
    categoryType: 'savings',
    hideDropdown: false,
    autoSelectCategory: 'vacation-travel',
    allowedCategories: ['vacation-travel', 'other-savings'],
    keywords: ['vacation', 'travel', 'holiday', 'trip', 'leisure']
  },
  'house-deposit': {
    categoryType: 'savings',
    hideDropdown: false,
    autoSelectCategory: 'home-savings',
    allowedCategories: ['home-savings'],
    keywords: ['house', 'home', 'property', 'deposit', 'mortgage', 'rent']
  },
  'car': {
    categoryType: 'savings',
    hideDropdown: false,
    autoSelectCategory: 'vehicle-savings',
    allowedCategories: ['vehicle-savings'],
    keywords: ['car', 'vehicle', 'auto', 'transportation']
  },
  'education': {
    categoryType: 'savings',
    hideDropdown: false,
    autoSelectCategory: 'education-savings',
    allowedCategories: ['education-savings'],
    keywords: ['education', 'school', 'college', 'course', 'training', 'tuition']
  },
  'wedding': {
    categoryType: 'savings',
    hideDropdown: false,
    autoSelectCategory: 'other-savings',
    allowedCategories: ['other-savings', 'vacation-travel'],
    keywords: ['wedding', 'event', 'celebration', 'party']
  },
  'retirement': {
    categoryType: 'savings',
    hideDropdown: false,
    autoSelectCategory: 'retirement-savings',
    allowedCategories: ['retirement-savings', 'investment-capital'],
    keywords: ['retirement', 'pension', 'investment', 'long-term']
  },
  'gadget': {
    categoryType: 'savings',
    hideDropdown: false,
    autoSelectCategory: 'other-savings',
    allowedCategories: ['other-savings'],
    keywords: ['gadget', 'electronics', 'tech', 'technology', 'phone', 'laptop']
  },

  // ============================================================================
  // SPENDING CAP GOALS (LIMIT - ALL EXPENSES) - Hide category dropdown
  // ============================================================================
  'monthly-spending-cap': {
    categoryType: 'all',
    hideDropdown: true,
    autoSelectCategory: null,
    allowedCategories: [],
    infoMessage: 'This goal tracks all your spending categories combined.',
    keywords: ['all', 'total', 'overall', 'monthly']
  },
  'weekly-spending-limit': {
    categoryType: 'all',
    hideDropdown: true,
    autoSelectCategory: null,
    allowedCategories: [],
    infoMessage: 'This goal tracks all your weekly spending combined.',
    keywords: ['all', 'total', 'overall', 'weekly']
  },

  // ============================================================================
  // CATEGORY LIMIT GOALS (LIMIT - SPECIFIC) - Filter to relevant expense categories
  // ============================================================================
  'groceries-limit': {
    categoryType: 'expense',
    hideDropdown: false,
    autoSelectCategory: 'groceries-food',
    allowedCategories: ['groceries-food'],
    keywords: ['groceries', 'food', 'grocery', 'supermarket', 'market']
  },
  'entertainment-limit': {
    categoryType: 'expense',
    hideDropdown: false,
    autoSelectCategory: 'entertainment',
    allowedCategories: ['entertainment', 'subscriptions-memberships'],
    keywords: ['entertainment', 'leisure', 'movies', 'games', 'streaming', 'hobbies']
  },
  'dining-limit': {
    categoryType: 'expense',
    hideDropdown: false,
    autoSelectCategory: 'dining-out',
    allowedCategories: ['dining-out', 'groceries-food'],
    keywords: ['dining', 'restaurant', 'takeaway', 'takeout', 'cafe', 'coffee']
  },
  'transport-limit': {
    categoryType: 'expense',
    hideDropdown: false,
    autoSelectCategory: 'transportation',
    allowedCategories: ['transportation'],
    keywords: ['transport', 'transportation', 'fuel', 'uber', 'taxi', 'bus', 'train']
  },
  'shopping-limit': {
    categoryType: 'expense',
    hideDropdown: false,
    autoSelectCategory: 'personal-care',
    allowedCategories: ['personal-care', 'entertainment'],
    keywords: ['shopping', 'retail', 'clothing', 'clothes', 'fashion', 'accessories']
  },

  // ============================================================================
  // DEBT PAYDOWN GOALS (ACCUMULATE) - Filter to debt categories
  // ============================================================================
  'credit-card-payoff': {
    categoryType: 'savings',
    hideDropdown: false,
    autoSelectCategory: 'debt-paydown',
    allowedCategories: ['debt-paydown'],
    keywords: ['credit-card', 'credit', 'card-payment', 'debt']
  },
  'student-loan-paydown': {
    categoryType: 'savings',
    hideDropdown: false,
    autoSelectCategory: 'debt-paydown',
    allowedCategories: ['debt-paydown', 'education-savings'],
    keywords: ['student-loan', 'student', 'loan', 'education']
  },
  'personal-loan-repayment': {
    categoryType: 'savings',
    hideDropdown: false,
    autoSelectCategory: 'debt-paydown',
    allowedCategories: ['debt-paydown'],
    keywords: ['personal-loan', 'loan', 'debt', 'repayment']
  },
  'mortgage-overpayment': {
    categoryType: 'savings',
    hideDropdown: false,
    autoSelectCategory: 'debt-paydown',
    allowedCategories: ['debt-paydown', 'home-savings'],
    keywords: ['mortgage', 'housing', 'home-loan', 'property']
  }
};

/**
 * Get category filter configuration for a goal template
 * @param {string} templateId - The goal template ID
 * @returns {object|null} - Filter configuration or null if not found
 */
export function getGoalCategoryFilter(templateId) {
  return GOAL_CATEGORY_FILTERS[templateId] || null;
}

// ============================================================================
// GOAL INTENT CATEGORY MAPPING
// ============================================================================
// Maps goal intents (from GOAL_INTENTS) to their category module types
// This determines what categories are shown in the dropdown based on selected goal type
// ============================================================================

export const GOAL_INTENT_CATEGORY_MAP = {
  'save-money': {
    categoryModule: 'savings',      // Show savings categories (emergency, vacation, etc.)
    hideDropdown: false,
    infoMessage: null
  },
  'spending-cap': {
    categoryModule: 'all',          // Tracks ALL expenses
    hideDropdown: true,
    infoMessage: 'This goal tracks all your spending categories combined.'
  },
  'category-limit': {
    categoryModule: 'expense',      // Show expense categories (groceries, entertainment, etc.)
    hideDropdown: false,
    infoMessage: null
  },
  'debt-paydown': {
    categoryModule: 'savings',      // Show savings categories (specifically debt-paydown)
    hideDropdown: false,
    infoMessage: null,
    autoSelectCategory: 'debt-paydown'
  },
  'custom-goal': {
    categoryModule: 'savings',      // Default to savings for custom goals
    hideDropdown: false,
    infoMessage: null
  }
};

/**
 * Get category configuration for a goal intent type
 * @param {string} intentId - The goal intent ID (save-money, spending-cap, etc.)
 * @returns {object} - Category configuration for the intent
 */
export function getGoalIntentCategoryConfig(intentId) {
  return GOAL_INTENT_CATEGORY_MAP[intentId] || GOAL_INTENT_CATEGORY_MAP['save-money'];
}
